import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, FileArchive, RefreshCw, AlertTriangle, User } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const STATUS_LABEL = {
    uploaded: "Uploaded",
    extracting: "Extracting files…",
    parsing_specs: "Parsing spec book…",
    ready: "Ready",
    converted: "Converted",
    failed: "Failed",
};

const STATUS_COLOR = {
    uploaded: "var(--brand-text-muted)",
    extracting: "var(--brand-warning)",
    parsing_specs: "var(--brand-warning)",
    ready: "var(--brand-success)",
    converted: "var(--brand-text-muted)",
    failed: "var(--brand-red)",
};

export default function BidIntake() {
    const [jobs, setJobs] = useState([]);
    const [err, setErr] = useState("");
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const fileRef = useRef(null);
    const pollRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get("/bid-intake/jobs");
            setJobs(data.jobs || []);
            // Keep polling while any job is still processing
            const pending = (data.jobs || []).some((j) =>
                ["uploaded", "extracting", "parsing_specs"].includes(j.status)
            );
            if (pending) {
                pollRef.current = setTimeout(load, 2500);
            } else if (pollRef.current) {
                clearTimeout(pollRef.current);
            }
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    useEffect(() => {
        load();
        return () => pollRef.current && clearTimeout(pollRef.current);
        // eslint-disable-next-line
    }, []);

    const upload = async (file) => {
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".zip")) {
            setErr("Only .zip uploads are supported.");
            return;
        }
        setErr("");
        setUploading(true);
        setProgress(0);
        try {
            const form = new FormData();
            form.append("file", file);
            await api.post("/bid-intake/upload", form, {
                onUploadProgress: (e) => {
                    if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
                },
            });
            await load();
        } catch (e) {
            setErr(apiErrorMessage(e, "Upload failed"));
        } finally {
            setUploading(false);
            setProgress(0);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    return (
        <div className="page-shell">
            <header className="fade-up flex items-start justify-between flex-wrap gap-3">
                <div>
                    <p className="eyebrow">Phase 7 — Pre-estimate automation</p>
                    <h1 className="h1 mt-3" data-testid="bid-intake-title">
                        Bid intake
                    </h1>
                    <p className="text-muted mt-2 max-w-[680px]">
                        Drop a bid-package zip (drawings + spec book). We categorise the
                        files, extract paint systems from the CSI 09 9x sections, and prep
                        a draft estimate with source attribution. Dimensions and counts
                        stay manual for now.
                    </p>
                </div>
                <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={load}
                    data-testid="bid-intake-refresh"
                >
                    <RefreshCw size={14} />
                    Refresh
                </button>
            </header>

            <div
                className="brand-card mt-6 fade-up"
                style={{ background: "var(--brand-cream)" }}
                data-testid="bid-intake-upload-card"
            >
                <div className="flex items-start gap-4 flex-wrap">
                    <div
                        className="flex items-center justify-center rounded-md"
                        style={{
                            width: 56,
                            height: 56,
                            background: "rgba(237,108,2,0.12)",
                            color: "var(--brand-warning)",
                        }}
                    >
                        <FileArchive size={28} />
                    </div>
                    <div className="flex-1 min-w-[260px]">
                        <p className="eyebrow">Upload zip</p>
                        <h3 className="h3 mt-1">Drop a bid package</h3>
                        <p className="text-muted text-sm mt-1">
                            Up to 1 GB. We'll read the project manual, find painting CSI
                            sections, and surface a gap report before you start.
                        </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".zip,application/zip"
                            onChange={(e) => upload(e.target.files?.[0])}
                            className="hidden"
                            data-testid="bid-intake-upload-input"
                        />
                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={uploading}
                            onClick={() => fileRef.current?.click()}
                            data-testid="bid-intake-upload-btn"
                        >
                            <Upload size={14} />
                            {uploading ? `Uploading… ${progress}%` : "Choose zip"}
                        </button>
                    </div>
                </div>
                {uploading && (
                    <div
                        className="mt-4 h-1.5 rounded-full overflow-hidden"
                        style={{ background: "rgba(0,0,0,0.08)" }}
                    >
                        <div
                            className="h-full"
                            style={{
                                width: `${progress}%`,
                                background: "var(--brand-warning)",
                                transition: "width 120ms linear",
                            }}
                        />
                    </div>
                )}
            </div>

            {err && (
                <div
                    className="brand-card mt-4"
                    style={{
                        borderColor: "var(--brand-red)",
                        color: "var(--brand-red)",
                    }}
                    data-testid="bid-intake-error"
                >
                    <AlertTriangle size={14} className="inline mr-1" />
                    {err}
                </div>
            )}

            <div className="brand-card mt-6 fade-up delay-1">
                <p className="eyebrow">Recent jobs</p>
                <div className="overflow-x-auto mt-3">
                    <table className="brand-table" data-testid="bid-intake-table">
                        <thead>
                            <tr>
                                <th>File</th>
                                <th>Status</th>
                                <th className="text-right">Files</th>
                                <th>Categories</th>
                                <th>Uploaded</th>
                            </tr>
                        </thead>
                        <tbody>
                            {jobs.map((j) => (
                                <tr
                                    key={j.id}
                                    style={{ cursor: "pointer" }}
                                    onClick={() =>
                                        (window.location.href = `/admin/bid-intake/${j.id}`)
                                    }
                                    data-testid={`bid-intake-row-${j.id}`}
                                >
                                    <td>
                                        <Link
                                            to={`/admin/bid-intake/${j.id}`}
                                            className="link-red"
                                        >
                                            {j.original_filename}
                                        </Link>
                                        {j.lead_id && j.lead_snapshot?.name && (
                                            <Link
                                                to={`/leads/${j.lead_id}`}
                                                onClick={(e) => e.stopPropagation()}
                                                className="ml-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                                                style={{
                                                    background: "rgba(196,30,58,0.08)",
                                                    color: "var(--brand-red)",
                                                    border: "1px solid rgba(196,30,58,0.25)",
                                                }}
                                                data-testid={`bid-intake-row-lead-${j.id}`}
                                                title={`Linked to lead ${j.lead_snapshot.name}`}
                                            >
                                                <User size={10} />
                                                {j.lead_snapshot.name}
                                            </Link>
                                        )}
                                    </td>
                                    <td>
                                        <span
                                            className="text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                                            style={{
                                                background: "rgba(0,0,0,0.04)",
                                                color: STATUS_COLOR[j.status],
                                                border: `1px solid ${STATUS_COLOR[j.status]}40`,
                                            }}
                                            data-testid={`status-${j.status}`}
                                        >
                                            {STATUS_LABEL[j.status] || j.status}
                                        </span>
                                    </td>
                                    <td className="text-right font-mono">
                                        {j.file_count || 0}
                                    </td>
                                    <td className="text-xs text-muted">
                                        {Object.entries(j.categories || {})
                                            .filter(([, n]) => n > 0)
                                            .map(([k, n]) => `${k} ${n}`)
                                            .join(" · ")}
                                    </td>
                                    <td className="text-xs text-muted">
                                        {fmtDateTime(j.created_at)}
                                    </td>
                                </tr>
                            ))}
                            {jobs.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={5}
                                        className="text-center text-muted py-8"
                                        data-testid="bid-intake-empty"
                                    >
                                        No bid packages uploaded yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
